import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, List, Settings as SettingsIcon, LogOut, MessageSquare, Lock } from 'lucide-react';
import BASE_URL from './config';
import { useToast, ToastContainer } from './Toast';
import AlertModal from './AlertModal';
import './index.css';
import logo from './images/logo.png';

function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Identity fields are locked once set — tracked separately so the UI
  // can show a read-only state even before the user tries to edit.
  const [nameLocked, setNameLocked] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const [notifyOnMessage, setNotifyOnMessage] = useState(true);
  const [notifyOnRideJoin, setNotifyOnRideJoin] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toasts, showToast, removeToast } = useToast();
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // Fetch user data - minimal approach to prevent flickering
  useEffect(() => {
    
    // Since ProtectedRoute handles authentication, just fetch user data once
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${BASE_URL}/check_login`, {
          method: 'GET',
          credentials: 'include',
          cache: 'no-cache'
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.isLoggedIn) {
            setUser(data.user);
            setName(data.userName || '');
            setEmail(data.email || '');
            setPhone(data.phone || '');
            // Lock identity fields if they are already set in the database.
            // The backend enforces this too — this is just a UI signal.
            setNameLocked(Boolean(data.userName));
            setEmailLocked(Boolean(data.email));
            setNotifyOnMessage(data.notifyOnMessage !== false);
            setNotifyOnRideJoin(data.notifyOnRideJoin !== false);
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []); // Only run once on mount

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setSaving(true);

    // Validate phone format - more comprehensive validation
    const phoneRegex = /^[+]?[1]?[\s-]?[(]?[0-9]{3}[)]?[\s-]?[0-9]{3}[\s-]?[0-9]{4}$/;
    const cleanPhone = phone.replace(/[\s-()+]/, '');
    
    if (!phoneRegex.test(phone) || cleanPhone.length < 10 || cleanPhone.length > 11) {
      setAlertModal({
        isOpen: true,
        title: 'Invalid Phone Number',
        message: 'Please enter a valid phone number (e.g., (203) 555-1234, 203-555-1234, or +1 203 555 1234).',
        type: 'error'
      });
      setSaving(false);
      return;
    }

    fetch(`${BASE_URL}/update_user_info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      // Only send identity fields when they are not yet locked (initial onboarding).
      // The backend enforces the same rule, but omitting them keeps the intent clear.
      body: JSON.stringify({
        username: user,
        phone,
        ...(nameLocked ? {} : { name }),
        ...(emailLocked ? {} : { email }),
        notifyOnMessage: notifyOnMessage,
        notifyOnRideJoin: notifyOnRideJoin,
      })
    })
      .then(response => response.json())
      .then(data => {
        showToast('Settings saved successfully!', 'success');
        setSaving(false);
      })
      .catch(error => {
        setAlertModal({
          isOpen: true,
          title: 'Save Failed',
          message: 'Failed to save settings. Please try again.',
          type: 'error'
        });
        setSaving(false);
      });
  }, [name, email, phone, user, nameLocked, emailLocked, notifyOnMessage, notifyOnRideJoin, showToast]);

  const goToHome = () => navigate('/feed');
  const goToRides = () => navigate('/rides');
  const goToNewRide = () => navigate('/newride');

  // Handle logout functionality
  function handleLogout() {
    if (user) {
      // Redirect to backend logout endpoint which will handle the logout and redirect to homepage
      window.location.href = `${BASE_URL}/logout`;
    } else {
      showToast('No user signed in.', 'warning');
    }
  }

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="skeleton-card">
      <div className="skeleton-header">
        <div className="skeleton skeleton-badge"></div>
      </div>
      <div className="skeleton skeleton-line long"></div>
      <div className="skeleton skeleton-line medium"></div>
      <div className="skeleton skeleton-line short"></div>
      <div className="skeleton skeleton-line medium"></div>
      <div className="skeleton skeleton-line short"></div>
      <div className="skeleton skeleton-button"></div>
    </div>
  );

  const header = (
    <header className="header">
      <div className="logo-container" onClick={goToHome}>
        <div className="logo-wrapper logo-light-mode">
          <img src={logo} alt="Logo" className="logo" />
        </div>
        <h1>Bulldog Carpool</h1>
      </div>
      <nav className="nav-buttons">
        <button className="nav-button" onClick={() => navigate('/feed')}>
          <List className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
          Feed
        </button>
        <button className="nav-button" onClick={goToRides}>
          <Car className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
          My Rides
        </button>
        <button className="nav-button" onClick={() => navigate('/messages')}>
          <MessageSquare className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
          Messages
        </button>
        <button className="nav-button active" onClick={() => navigate('/settings')}>
          <SettingsIcon className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
          Settings
        </button>
        <button className="nav-button" onClick={handleLogout}>
          <LogOut className="nav-icon w-4 h-4" stroke="#00356B" strokeWidth={1.5} />
          Log Out
        </button>
      </nav>
    </header>
  );

  return (
    <div className="app-container">
      {header}

      <button className="add-ride-button" onClick={goToNewRide}>Add Ride</button>

      <div className="settings-page-container">
        <div className="settings-content">
          <div className="settings-header">
            <h1 className="settings-title">Account Settings</h1>
            <p className="settings-subtitle">Manage your profile information</p>
          </div>

          {loading ? (
            <div className="settings-form-container">
              <SkeletonCard />
            </div>
          ) : (
          <div className="settings-card">
            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-fields">
                {/* Identity field: Full Name */}
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name
                    {nameLocked && (
                      <span className="locked-badge">
                        <Lock size={12} strokeWidth={2} />
                        Yale account
                      </span>
                    )}
                  </label>
                  {nameLocked ? (
                    <>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        className="form-input form-input-locked"
                        disabled
                        aria-label="Full name — locked to Yale account"
                      />
                      <small className="form-help form-help-locked">
                        Tied to your Yale account and cannot be changed.
                      </small>
                    </>
                  ) : (
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                      className="form-input"
                      required
                    />
                  )}
                </div>

                {/* Identity field: Yale Email */}
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email
                    {emailLocked && (
                      <span className="locked-badge">
                        <Lock size={12} strokeWidth={2} />
                        Yale account
                      </span>
                    )}
                  </label>
                  {emailLocked ? (
                    <>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        className="form-input form-input-locked"
                        disabled
                        aria-label="Email — locked to Yale account"
                      />
                      <small className="form-help form-help-locked">
                        Tied to your Yale account and cannot be changed.
                      </small>
                    </>
                  ) : (
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@yale.edu"
                      className="form-input"
                      required
                    />
                  )}
                </div>

                {/* Editable field: Phone */}
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter your phone number"
                    className="form-input"
                    required
                  />
                  <small className="form-help">Shared with ride participants to coordinate pickups</small>
                </div>
              </div>

              <div className="form-divider"></div>

              <div className="form-fields">
                <h3 className="settings-section-title">Email Notifications</h3>
                <div className="form-group toggle-group">
                  <label className="toggle-label">
                    <span>Email me when I receive messages in rides</span>
                    <button
                      type="button"
                      className={`toggle-switch ${notifyOnMessage ? 'toggle-on' : 'toggle-off'}`}
                      onClick={() => setNotifyOnMessage(!notifyOnMessage)}
                      aria-pressed={notifyOnMessage}
                    >
                      <span className="toggle-knob" />
                    </button>
                  </label>
                </div>
                <div className="form-group toggle-group">
                  <label className="toggle-label">
                    <span>Email me when someone joins my ride</span>
                    <button
                      type="button"
                      className={`toggle-switch ${notifyOnRideJoin ? 'toggle-on' : 'toggle-off'}`}
                      onClick={() => setNotifyOnRideJoin(!notifyOnRideJoin)}
                      aria-pressed={notifyOnRideJoin}
                    >
                      <span className="toggle-knob" />
                    </button>
                  </label>
                </div>
              </div>

              <div className="form-divider"></div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={goToRides}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
          )}
        </div>
      </div>

      {/* Toast and Modal Components */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}

export default Settings;
